/**
 * @file RepPricingView.js
 * @version 1.0.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-11-01
 * @description Rep Pricing — the office's one place for everything sent to
 *              reps (Cam, 28 Oct 2026 — P3: "Send to Reps and Asked a Rep
 *              should be merged in a more intuitive way - perhaps into one
 *              screen 'Rep Pricing'. It should be clearer on what has been
 *              sent, is due to be sent etc."; the redesign's picks):
 *
 *   D3  A second view inside Pricing & Costing: "Lines | Rep Pricing" at the
 *       top (PricingCostingViewSwitch, below — the switch shows how many
 *       requests are due to go to reps). The Lines view's dates and filters
 *       carry over (the same `filters`), the chosen view is remembered per
 *       tab (useRepPricingSwitch → usePersistedState 'pricingCosting.view').
 *   D4  Rep first: reps down the left with their numbers (to send, how many
 *       already delivered, waiting and since when, priced by rep, flags);
 *       the chosen rep's To send, Waiting, Priced by rep, Flagged and Sends
 *       on the right; "All reps" shows everything, the whole send log too.
 *   D5  By delivery, NO reminders: To send lists every request with no price
 *       and not with a rep — delivered first (red), then the next 7 days,
 *       later, no date. Review & send opens the Send to reps window
 *       (RepSendModal). Waiting is grouped by the send the lines went in,
 *       oldest first, with Download / Resend (as sent) / Send again
 *       (today's figures).
 *   D9  Priced by rep: rep prices from their spreadsheet AND their own
 *       screen. Approve (confirms the sale price), Hold, Ask again (a second
 *       price check, through the send window). On approve / hold the person
 *       decides whether the rep is told (RepReviewDialog — Cam: "Allow user
 *       to decide"). A rep's request for a contract shows as "Contract asked
 *       for" and opens Contract these prices (ContractPricesModal) here, or
 *       Not now. Import rep replies lives here.
 *   D11 Flagged: a pile here and the flag on the line — no Alerts count (the
 *       notice now fires only when a price is confirmed over an open flag).
 *       Dealt with… picks the document that settled it (RepFlagResolveModal).
 *   D3 note: opening a request reads its earlier sales with their cost and
 *       margin (POST /rep-pricing/previous).
 *
 *   1.0.1 (pc-lines, 28 Oct 2026): the period choices may come from Pricing &
 *   Costing (`rangePresets` — its rangeOptions), so the two views offer the
 *   same months; the range itself is the person's remembered one
 *   (useScreenPreference 'pricingCosting.range'), set from either view.
 *   1.0.2 (Cam, 30 Oct 2026 — q21): the heading's controls on one line, as
 *   the Lines view's — Clear Filters left of the switch, Import rep replies
 *   and Refresh right of it (they wrapped below it).
 *
 *   Data: GET /api/pricing-costing/rep-pricing?from&to (services/
 *   repPricingService.summary); flags dealt with: GET /rep-flags. Rules
 *   pure in utils/repPricingView.js and utils/repPricing.js. Costs only with
 *   costs.view (the server leaves them out). Reps never reach it (the route
 *   refuses REP).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './RepPricingView.css';
import Input from './Input';
import DateRangeFilter from './DateRangeFilter';
import MultiSelectDropdown from './MultiSelectDropdown';
import SearchHelp from './SearchHelp';
import TickCheckbox from './TickCheckbox';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import RepSendModal from './RepSendModal';
import RepRepliesImportModal from './RepRepliesImportModal';
import ContractPricesModal from './ContractPricesModal';
import RepReviewDialog from './RepReviewDialog';
import RepFlagResolveModal from './RepFlagResolveModal';
import RepRequestHistory, { ResendDialog, resendSend, downloadSendWorkbook, qtyWithUnit } from './RepRequestHistory';
import { useSlideOutButton } from './SlideOutButton';
import { useEntityModal } from './EntityModalProvider';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';
import useReadOnly from '../hooks/useReadOnly';
import { clearFiltersClass } from '../utils/activeFilters';
import { formatDateTimeLondon } from '../utils/dateFormatters';
import { money, ukDate, ukDateFull, PRODUCT_CATEGORIES } from '../utils/pricingCosting';
import { toSendCount, REPLY_SOURCE_LABELS } from '../utils/repPricing';
import {
  REP_ALL, REP_NONE, TABS, forRep, filterRequests, filterFlags, filterContractAsks, railOf, tabCounts,
  dueSections, waitingBySend, lineIdsOf, costsOf, resolvedText
} from '../utils/repPricingView';

const METHOD_ICONS = { ours: 'local_shipping', customer_collects: 'hail', third_party: 'conveyor_belt', direct: 'factory' };

/** This month, the two before and the two after (Cam's answer, 28 Oct 2026), then Custom. */
export function monthPresets(today = new Date()) {
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const name = (d) => d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const at = (k) => {
    const first = new Date(today.getFullYear(), today.getMonth() + k, 1);
    return { first, from: iso(first), to: iso(new Date(first.getFullYear(), first.getMonth() + 1, 0)) };
  };
  const v = [
    ['month-2', -2, null], ['last-month', -1, 'Last month'], ['this-month', 0, 'This month'], ['next-month', 1, 'Next month'], ['month+2', 2, null]
  ].map(([value, k, word]) => { const m = at(k); return { value, label: word ? `${word} (${name(m.first)})` : name(m.first), from: m.from, to: m.to }; });
  return [...v, { value: 'custom', label: 'Custom' }];
}

/** "£287.00/T", "£287.00–£290.00/T", or null. */
function figure(r, per = '') {
  if (!r) return null;
  if (r.value !== null && r.value !== undefined) return `${money(r.value)}${per}`;
  if (r.min !== null && r.min !== undefined) return `${money(r.min)}–${money(r.max)}${per}`;
  return null;
}

// ---------------------------------------------------------------------------
// The switch (D3) — rendered at the top of both views
// ---------------------------------------------------------------------------

/** "Lines | Rep Pricing (n to send)". */
export function PricingCostingViewSwitch({ view, onChange, toSend = 0 }) {
  return (
    <div className="pc-viewsw" role="group" aria-label="View">
      <button type="button" aria-pressed={view !== 'reps'} onClick={() => onChange('lines')}
        title="Every line's cost and sale price">
        <span className="material-symbols-rounded">table_rows</span>Lines
      </button>
      <button type="button" aria-pressed={view === 'reps'} onClick={() => onChange('reps')}
        title={`Everything sent to reps — ${toSend} request${toSend === 1 ? '' : 's'} due to go`}>
        <span className="material-symbols-rounded">forward_to_inbox</span>Rep Pricing
        {toSend > 0 && <span className="pc-viewsw-count">{toSend} to send</span>}
      </button>
    </div>
  );
}

/**
 * For Pricing & Costing (one marked block there): the view chosen (kept per
 * tab), and the switch counting the requests due to go to reps from the
 * Lines view's own rows.
 */
export function useRepPricingSwitch(rows) {
  const [view, setView] = usePersistedState('pricingCosting.view', 'lines');
  const toSend = useMemo(() => toSendCount(rows || []), [rows]);
  return { view, setView, toSend, element: <PricingCostingViewSwitch view={view} onChange={setView} toSend={toSend} /> };
}

// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------

const EMPTY = { reps: [], requests: [], flags: [], contract_asks: [], can: {}, ready: {}, today: null, range: null, loaded: false };

// `rangePresets` (pc-lines, 28 Oct 2026): Pricing & Costing's own period
// choices (utils/pricingCostingView.rangeOptions), so both views offer the
// same months and share the person's remembered range.
function RepPricingView({ filters, setFilters, onView, clearedFilters = null, rangePresets = null }) {
  const readOnly = useReadOnly();
  const { openEntity } = useEntityModal();
  const latest = useLatestRequest();
  const [repKey, setRepKey] = usePersistedState('repPricing.rep', REP_ALL);
  const [tab, setTab] = usePersistedState('repPricing.tab', 'send');
  const [showResolved, setShowResolved] = usePersistedState('repPricing.showResolved', false);
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [ticked, setTicked] = useState({});
  const [open, setOpen] = useState({});
  const [previous, setPrevious] = useState({});
  const [resolved, setResolved] = useState({ loading: false, flags: [] });
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [alert, setAlert] = useState({ isOpen: false, message: '', type: 'info' });
  const [ask, setAsk] = useState(null);
  const [sendLines, setSendLines] = useState(null);
  const [review, setReview] = useState(null);
  const [resolving, setResolving] = useState(null);
  const [resending, setResending] = useState(null);
  const [importing, setImporting] = useState(false);
  const [contractLines, setContractLines] = useState(null);
  useSlideOutButton([tab, repKey, busy, data.loaded, Object.keys(ticked).length]);

  const load = useCallback(async () => {
    const ticket = latest.begin();
    setLoading(true);
    setLoadError(null);
    try {
      const qs = new URLSearchParams({ from: filters.from || '', to: filters.to || '' });
      const res = await fetch(`${API_URL}/api/pricing-costing/rep-pricing?${qs}`, { credentials: 'include' });
      const body = await res.json();
      if (!latest.isLatest(ticket)) return;
      if (!res.ok || !body.success) throw new Error(body.error || 'Rep Pricing could not be loaded');
      setData({ ...EMPTY, ...body, loaded: true });
    } catch (err) {
      if (latest.isLatest(ticket)) setLoadError(err.message);
    }
    if (latest.isLatest(ticket)) setLoading(false);
  }, [filters.from, filters.to]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  useEffect(() => { setTicked({}); }, [tab, repKey]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), toast.undo ? 9000 : 5000);
    return () => clearTimeout(t);
  }, [toast]);

  // ---- what the filters keep ----
  const requests = useMemo(() => filterRequests(data.requests, filters), [data.requests, filters]);
  const flags = useMemo(() => filterFlags(data.flags, filters), [data.flags, filters]);
  const asks = useMemo(() => filterContractAsks(data.contract_asks, filters), [data.contract_asks, filters]);
  const rail = useMemo(() => railOf({ reps: data.reps, requests, flags, contractAsks: asks, filters }), [data.reps, requests, flags, asks, filters]);
  const entry = rail.find(e => e.key === repKey) || rail[0];
  useEffect(() => {
    if (data.loaded && repKey !== REP_ALL && !rail.some(e => e.key === repKey)) setRepKey(REP_ALL);
  }, [data.loaded, rail, repKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const mine = useMemo(() => requests.filter(q => forRep(repKey, q.rep_id)), [requests, repKey]);
  const toSend = mine.filter(q => q.bucket === 'to_send');
  const waiting = mine.filter(q => q.bucket === 'waiting');
  const priced = mine.filter(q => q.bucket === 'priced_by_rep');
  const myAsks = asks.filter(a => forRep(repKey, a.rep_id));
  const myFlags = flags.filter(f => forRep(repKey, f.rep_id));
  const counts = tabCounts(entry);
  const dueCount = useMemo(() => requests.filter(q => q.bucket === 'to_send').length, [requests]);
  const can = data.can || {};
  const writeBlock = readOnly ? 'View only' : !can.confirm_sales ? 'Needs permission to price sales orders' : null;

  // Dealt-with flags, when asked for.
  useEffect(() => {
    if (tab !== 'flagged' || !showResolved) return;
    let gone = false;
    (async () => {
      setResolved({ loading: true, flags: [] });
      try {
        const q = new URLSearchParams({ status: 'resolved', limit: '100' });
        if (repKey === REP_NONE) q.set('repId', 'none');
        else if (repKey !== REP_ALL) q.set('repId', repKey);
        const res = await fetch(`${API_URL}/api/pricing-costing/rep-flags?${q}`, { credentials: 'include' });
        const body = await res.json();
        if (!gone) setResolved({ loading: false, flags: body.flags || [] });
      } catch (err) {
        if (!gone) setResolved({ loading: false, flags: [], error: err.message });
      }
    })();
    return () => { gone = true; };
  }, [tab, showResolved, repKey, data.loaded]);

  const showToast = (message, undo = null) => setToast({ message, undo, at: Date.now() });

  // ---- ticks ----
  const tickedOf = (list) => list.filter(q => ticked[q.key]);
  const setTick = (keys, on) => setTicked(prev => {
    const next = { ...prev };
    keys.forEach(k => { if (on) next[k] = true; else delete next[k]; });
    return next;
  });

  // ---- opening a request: its lines and earlier sales (D3 note) ----
  const toggleOpen = async (q) => {
    const now = !open[q.key];
    setOpen(prev => ({ ...prev, [q.key]: now }));
    if (!now || previous[q.key]) return;
    setPrevious(prev => ({ ...prev, [q.key]: { loading: true } }));
    try {
      const res = await fetch(`${API_URL}/api/pricing-costing/rep-pricing/previous`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ lineIds: q.line_ids })
      });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Earlier sales could not be read');
      const first = (body.requests || []).find(r => r.line_ids.some(id => q.line_ids.includes(id)));
      setPrevious(prev => ({ ...prev, [q.key]: { sales: first ? first.previous_sales : [], costs: body.costs_shown } }));
    } catch (err) {
      setPrevious(prev => ({ ...prev, [q.key]: { error: err.message } }));
    }
  };

  // ---- actions ----
  const post = async (url, body) => {
    const res = await fetch(`${API_URL}${url}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body)
    });
    const out = await res.json();
    if (!res.ok || !out.success) throw new Error(out.error || 'Nothing was changed');
    return out;
  };

  const sendBlock = (list) => (readOnly ? 'View only'
    : !can.confirm_sales ? 'Asking reps for prices needs permission to price sales orders'
      : !list.length ? 'Nothing to send'
        : !list.some(q => q.rep_id) ? 'No rep on the account — set a rep on the customer to send' : null);
  const openSend = (list, resendOf = null) => {
    const withRep = list.filter(q => q.rep_id);
    setSendLines({ ids: lineIdsOf(withRep), costs: costsOf(withRep), resendOf });
  };

  const notForReps = async (list, exclude = true) => {
    setBusy(true);
    try {
      const ids = lineIdsOf(list);
      await post('/api/pricing-costing/rep-requests/exclude', { lineIds: ids, exclude });
      showToast(exclude
        ? `${ids.length} line${ids.length === 1 ? '' : 's'} taken out of To send — priced without asking a rep`
        : 'Put back in To send', exclude ? () => notForReps(list, false) : null);
    } catch (err) {
      setAlert({ isOpen: true, type: 'error', message: err.message });
    } finally {
      setBusy(false);
      load();
    }
  };

  const undoConfirm = async (items) => {
    setBusy(true);
    setToast(null);
    try {
      const out = await post('/api/pricing-costing/unconfirm', { items });
      const bad = (out.results || []).filter(r => r.outcome === 'failed');
      if (bad.length) setAlert({ isOpen: true, type: 'warning', message: `Not undone:\n${bad.map(b => `• ${b.message}`).join('\n')}` });
      else showToast(`Undone — ${items.length} put back`);
    } catch (err) {
      setAlert({ isOpen: true, type: 'error', message: err.message });
    } finally {
      setBusy(false);
      load();
    }
  };

  const reviewDone = (action, body) => {
    setReview(null);
    const results = body.results || [];
    const good = results.filter(r => r.outcome === (action === 'approve' ? 'confirmed' : 'held'));
    const bad = results.filter(r => !good.includes(r));
    const mails = body.emails || [];
    const mailWords = mails.length
      ? ` ${mails.map(m => (m.outcome === 'sent' ? `Emailed ${m.to}.` : m.outcome === 'skipped' ? '' : `Email to ${m.to || 'the rep'} NOT sent: ${m.message}.`)).filter(Boolean).join(' ')}`
      : '';
    setTicked({});
    if (bad.length || mails.some(m => m.outcome === 'failed')) {
      setAlert({ isOpen: true, type: good.length ? 'warning' : 'error',
        message: `${good.length} ${action === 'approve' ? 'approved' : 'held'}${bad.length ? `, ${bad.length} not:\n\n${bad.slice(0, 15).map(b => `• ${b.message}`).join('\n')}` : ''}${mailWords ? `\n\n${mailWords.trim()}` : ''}` });
    } else {
      showToast(`${good.length} rep price${good.length === 1 ? '' : 's'} ${action === 'approve' ? 'approved — the sale price is confirmed' : 'held for review'}.${mailWords}`,
        action === 'approve' && good.length ? () => undoConfirm(good.map(r => ({ key: r.key, side: 'sale' }))) : null);
    }
    load();
  };

  const contractBlock = readOnly ? 'View only' : !can.contract ? (can.contract_block || "Needs 'Edit contract pricing'") : null;
  const openContract = (a) => {
    setContractLines({
      lines: a.lines.map(l => ({ id: l.line_id, price: l.price ?? (a.price ? a.price.value : null), from: "Rep's price (asked for a contract)" })),
      notLines: []
    });
  };
  const notNow = async (a) => {
    setAsk(null);
    setBusy(true);
    try {
      await post('/api/pricing-costing/rep-contract-requests/dismiss', { lineIds: a.line_ids });
      showToast(`Not now — ${a.rep_name || 'the rep'}'s request for a contract on ${a.product?.name || 'this product'} put aside`);
    } catch (err) {
      setAlert({ isOpen: true, type: 'error', message: err.message });
    } finally {
      setBusy(false);
      load();
    }
  };

  const doResend = async () => {
    const r = resending;
    setResending(null);
    setBusy(true);
    try {
      showToast(await resendSend(r));
    } catch (err) {
      setAlert({ isOpen: true, type: 'error', message: err.message });
    } finally {
      setBusy(false);
      load();
    }
  };

  const openOrder = (id) => (e) => { if (e) e.stopPropagation(); openEntity('customer_order', id, { onClose: load }); };
  const openContact = (id) => (e) => { if (e) e.stopPropagation(); openEntity('contact', id, { onClose: load }); };
  const openInvoice = (id) => (e) => { if (e) e.stopPropagation(); openEntity('invoice', id, { onClose: load }); };

  const filtersApplied = !!(filters.search || (filters.reps || []).length || (filters.categories || []).length);
  const clearFilters = () => setFilters(prev => ({ ...prev, search: '', reps: [], categories: [], ...(clearedFilters ? { status: clearedFilters.status } : {}) }));
  const repName = entry && entry.rep_id ? entry.name : null;
  const firstName = repName ? repName.split(' ')[0] : null;

  // ---------------------------------------------------------------------------
  // Rows
  // ---------------------------------------------------------------------------

  const requestRow = (q, { right, actions = null, tickable = true }) => {
    const isOpen = !!open[q.key];
    const prev = previous[q.key];
    const orders = q.orders || [];
    return (
      <div key={q.key} className={`rp-rq ${isOpen ? 'open' : ''} ${ticked[q.key] ? 'ticked' : ''}`}>
        <div className="rp-rq-main" onClick={() => toggleOpen(q)} role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') toggleOpen(q); }} aria-expanded={isOpen}>
          <div className="rp-rq-tick" onClick={(e) => e.stopPropagation()}>
            {tickable ? <TickCheckbox id={`rp-t-${q.key}`} checked={!!ticked[q.key]} onChange={(e) => setTick([q.key], e.target.checked)} ariaLabel="Tick this request" />
              : <span className="material-symbols-rounded rp-muted">{isOpen ? 'expand_less' : 'expand_more'}</span>}
          </div>
          <div className="rp-c">
            <span className="rp-t1">
              {q.customer?.account_no && <button type="button" className="account-badge-inline rp-acc" onClick={openContact(q.customer.id)}>{q.customer.account_no}</button>}
              <button type="button" className="rp-link rp-name" onClick={openContact(q.customer?.id)}>{q.customer?.name}</button>
            </span>
            <span className="rp-t2">{q.product?.name}{q.product?.code ? <span className="rp-muted"> · {q.product.code}</span> : null}
              {(q.additives || []).length ? <span className="rp-muted"> + {q.additives.join(' + ')}</span> : null}</span>
          </div>
          <div className="rp-c">
            <span className="rp-t2 rp-orders">
              {orders.slice(0, 2).map((o, i) => (
                <span key={o.line_id}>{i ? ' · ' : ''}<button type="button" className="rp-link" onClick={openOrder(o.order_id)}>{o.order_number}</button>
                  {' '}{qtyWithUnit(o.quantity, q.unit)}{o.delivery_date ? ` · ${ukDate(o.delivery_date)}` : ''}</span>
              ))}
              {orders.length > 2 && <span className="rp-muted"> · and {orders.length - 2} more</span>}
            </span>
            <span className="rp-chips">
              {q.delivery_method && (
                <span className="rp-chip"><span className="material-symbols-rounded">{METHOD_ICONS[q.delivery_method.kind] || 'local_shipping'}</span>{q.delivery_method.label}</span>
              )}
              {q.held && <span className="rp-chip warn">Held</span>}
              {q.contract_request && <span className="rp-chip info" title={`${q.contract_request.rep_name || 'The rep'} asked for this price to be made a contract price`}><span className="material-symbols-rounded">handshake</span>Contract asked for</span>}
            </span>
          </div>
          <div className="rp-c rp-sup">
            <span className="rp-t2">{(q.suppliers || []).join(', ') || <span className="rp-muted">—</span>}</span>
            {q.unit_flags > 0 && <span className="rp-warn"><span className="material-symbols-rounded">warning</span>Units don't agree on {q.unit_flags} line{q.unit_flags === 1 ? '' : 's'}</span>}
            {!q.unit && <span className="rp-warn"><span className="material-symbols-rounded">warning</span>No unit on the line</span>}
          </div>
          <div className="rp-c rp-right">{right}</div>
          {actions && <div className="rp-c rp-acts" onClick={(e) => e.stopPropagation()}>{actions}</div>}
        </div>
        {isOpen && (
          <div className="rp-rq-detail">
            <table className="line-items-table rp-lines">
              <thead><tr><th>Order</th><th>Delivery</th><th>Quantity</th><th>Supplier</th>{can.see_costs && <th>Cost</th>}<th>Sent to the rep</th>{q.bucket === 'priced_by_rep' && <th>Rep's price</th>}</tr></thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.line_id}>
                    <td><button type="button" className="rp-link" onClick={openOrder(o.order_id)}>{o.order_number}</button></td>
                    <td className={o.delivered ? 'rp-due' : ''}>{o.delivery_date ? ukDateFull(o.delivery_date) : '—'}{o.delivered ? ' · delivered' : ''}</td>
                    <td>{qtyWithUnit(o.quantity, q.unit)}{o.supplied !== null && o.supplied !== undefined ? <span className="rp-muted"> · supplied {qtyWithUnit(o.supplied, q.unit)}</span> : null}</td>
                    <td>{o.supplier || '—'}</td>
                    {can.see_costs && <td>{o.cost !== null && o.cost !== undefined ? `${money(o.cost)}${q.per || ''}` : <span className="rp-muted">no cost yet</span>}</td>}
                    <td>{o.sent_at ? formatDateTimeLondon(o.sent_at) : <span className="rp-muted">not yet</span>}</td>
                    {q.bucket === 'priced_by_rep' && <td>{o.rep_price !== null && o.rep_price !== undefined ? `${money(o.rep_price)}${q.per || ''}` : '—'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="rp-prev">
              <span className="rp-prev-h">Earlier sales to {q.customer?.name}</span>
              {!prev || prev.loading ? <span className="rp-muted">Reading…</span>
                : prev.error ? <span className="rp-warn">{prev.error}</span>
                  : !prev.sales.length ? <span className="rp-muted">None in the last 24 months</span>
                    : prev.sales.map((p, i) => (
                      <span key={i} className="rp-prev-row">
                        <b>{money(p.price)}{p.unit ? `/${p.unit}` : ''}</b>
                        {p.delivery_date ? ` — delivered ${ukDateFull(p.delivery_date)}` : ''}
                        {p.invoice_no ? ` (${p.invoice_no})` : p.order_number ? ` (${p.order_number})` : ''}
                        {p.cost !== undefined && p.cost !== null ? ` · cost ${money(p.cost)}${p.unit ? `/${p.unit}` : ''}${p.cost_ref ? ` (${p.cost_ref})` : ''}` : ''}
                        {p.margin !== undefined && p.margin !== null ? ` · margin ${money(p.margin)}${p.unit ? `/${p.unit}` : ''}${p.margin_pc !== null && p.margin_pc !== undefined ? ` (${p.margin_pc}% on cost)` : ''}` : ''}
                        {p.recipe_match === false ? <span className="rp-muted"> · another recipe</span> : null}
                        {p.method ? <span className="rp-muted"> · {p.method.label}</span> : null}
                      </span>
                    ))}
            </div>
            {(q.notes || []).length > 0 && <p className="rp-rep-note">“{q.notes.join(' · ')}” <span className="rp-muted">— {q.rep_name}</span></p>}
          </div>
        )}
      </div>
    );
  };

  const costRight = (q, word) => {
    if (!can.see_costs) return null;
    const f = figure(q.cost, q.per);
    return (<><span className="rp-num">{f || 'no cost yet'}</span><span className="rp-t2">{word}</span></>);
  };

  const tickAll = (list) => {
    const all = list.length > 0 && list.every(q => ticked[q.key]);
    return (
      <TickCheckbox id={`rp-all-${tab}`} checked={all} onChange={(e) => setTick(list.map(q => q.key), e.target.checked)}
        label={all ? `All ${list.length} ticked` : `Tick all ${list.length}`} />
    );
  };

  // ---------------------------------------------------------------------------
  // Tabs
  // ---------------------------------------------------------------------------

  const sendTab = () => {
    const sections = dueSections(toSend, data.today);
    const chosen = tickedOf(toSend);
    const list = chosen.length ? chosen : toSend;
    const lines = lineIdsOf(list).length;
    const block = sendBlock(list);
    const ls = entry && entry.last_send;
    return {
      body: !toSend.length ? <div className="rp-empty">Nothing to send{repName ? ` to ${repName}` : ''} for these dates — every line has a price, or is with a rep.</div> : (
        <>
          <div className="rp-listhead">{tickAll(toSend)}<span className="rp-muted">Soonest delivery first; delivered and still not priced at the top.</span></div>
          {sections.map(s => (
            <React.Fragment key={s.key}>
              <div className={`rp-grp ${s.tone}`}><span className="material-symbols-rounded">{s.icon}</span>{s.label} · {s.requests.length} request{s.requests.length === 1 ? '' : 's'}
                {s.key === 'delivered' ? ` · ${lineIdsOf(s.requests).length} lines` : ''}</div>
              {s.requests.map(q => requestRow(q, {
                right: costRight(q, 'cost to ask at'),
                actions: repKey === REP_ALL ? <span className="rp-chip">{q.rep_name || 'No rep'}</span> : null
              }))}
            </React.Fragment>
          ))}
        </>
      ),
      foot: (
        <>
          <span className="rp-muted">{ls ? `Last send${firstName ? ` to ${firstName}` : ''}: ${formatDateTimeLondon(ls.at)}${ls.line_count ? ` · ${ls.line_count} lines` : ''}${ls.by ? ` · by ${ls.by}` : ''}` : (repName ? `Nothing sent to ${firstName} yet` : `${dueCount} request${dueCount === 1 ? '' : 's'} due to go to reps`)}</span>
          <span className="rp-foot-btns">
            <div><button type="button" className="btn-slideout always-out" disabled={busy || !!writeBlock || !list.length} title={writeBlock || 'Take these out of To send — they will be priced without asking a rep'}
              onClick={() => notForReps(list)}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">do_not_disturb_on</span></div>
              <span className="btn-label">Not for reps ({lines})</span>
            </button></div>
            <div><button type="button" className="btn-slideout always-out btn-invert" disabled={busy || !!block} title={block || 'One email per rep, each checked before it goes'}
              onClick={() => openSend(list)}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">forward_to_inbox</span></div>
              <span className="btn-label">Review &amp; send {chosen.length ? `${chosen.length} ticked (${lines} line${lines === 1 ? '' : 's'})` : `${lines} line${lines === 1 ? '' : 's'}`}{firstName ? ` to ${firstName}` : ''}</span>
            </button></div>
          </span>
        </>
      )
    };
  };

  const waitingTab = () => {
    const groups = waitingBySend(waiting);
    return {
      body: !waiting.length ? <div className="rp-empty">Nothing waiting{repName ? ` on ${repName}` : ''} — no line sent for these dates is without an answer.</div> : (
        groups.map(g => {
          const chosen = tickedOf(g.requests);
          const list = chosen.length ? chosen : g.requests;
          const lines = lineIdsOf(list).length;
          const send = g.send ? { id: g.send.send_id, sent_at: g.send.at, has_workbook: !!g.send.has_workbook, workbook_filename: null } : null;
          const resendBlock = !can.confirm_sales ? 'Asking reps for prices needs permission to price sales orders' : !send ? 'This send was not kept — use Send again' : null;
          const toAddr = (rail.find(e => e.rep_id && String(e.rep_id) === String(g.requests[0].rep_id)) || {}).email || '';
          return (
            <div key={g.key} className="rp-sendgrp">
              <div className="rp-grp">
                <span className="material-symbols-rounded">schedule_send</span>
                {g.send ? `In the send of ${formatDateTimeLondon(g.at)}` : `Asked ${g.at ? formatDateTimeLondon(g.at) : '—'} (before sends were kept)`}
                {g.by ? ` · by ${g.by}` : ''} · {g.requests.length} request{g.requests.length === 1 ? '' : 's'}, {g.lines} line{g.lines === 1 ? '' : 's'} still waiting
                {g.send && g.send.second_check ? ' · second price check' : ''}
                <span className="rp-grp-acts">
                  <button type="button" className="rp-btn" disabled={!send || !send.has_workbook} title={!send ? 'This send was not kept' : !send.has_workbook ? 'No spreadsheet was kept for this send' : 'Download the spreadsheet as it went'}
                    onClick={() => downloadSendWorkbook(send).catch(err => setAlert({ isOpen: true, type: 'error', message: err.message }))}>
                    <span className="material-symbols-rounded">download</span>Download</button>
                  <button type="button" className="rp-btn" disabled={busy || !!resendBlock || readOnly} title={resendBlock || 'Resend — the same email and spreadsheet again'}
                    onClick={() => setResending({ send, to: toAddr, cc: '', sendAs: 'me' })}>
                    <span className="material-symbols-rounded">redo</span>Resend…</button>
                  <button type="button" className="rp-btn" disabled={busy || !!sendBlock(list)} title={sendBlock(list) || 'A fresh request of these lines with today\'s figures, checked before it goes'}
                    onClick={() => openSend(list, send && /^\d+$/.test(String(send.id)) ? Number(send.id) : null)}>
                    <span className="material-symbols-rounded">forward_to_inbox</span>Send again ({lines})</button>
                </span>
              </div>
              {g.requests.map(q => requestRow(q, {
                right: (<>{costRight(q, 'asked at')}<span className="rp-t2">sent {q.sent_at ? ukDate(q.sent_at) : '—'}</span></>),
                actions: repKey === REP_ALL ? <span className="rp-chip">{q.rep_name || 'No rep'}</span> : null
              }))}
            </div>
          );
        })
      ),
      foot: <span className="rp-muted">Oldest first. Resend sends the same email and file again; Send again asks with today's figures, only for the lines still waiting. No reminders are sent by themselves.</span>
    };
  };

  const pricedTab = () => {
    const chosen = tickedOf(priced);
    const list = chosen.length ? chosen : priced;
    const lines = lineIdsOf(list).length;
    const reviewBlock = writeBlock || (data.ready.rep_review === false ? 'Not ready — migration pricing_costing_rep_review.sql has not run' : null);
    const priceRight = (q) => (
      <>
        <span className="rp-num rp-price">{figure(q.rep_prices, q.per) || '—'}</span>
        <span className="rp-t2" title={q.rep_price && q.rep_price.source !== 'rep_view' ? `Imported by ${q.rep_price.by || 'the office'}` : ''}>
          {q.rep_price ? `${REPLY_SOURCE_LABELS[q.rep_price.source] || REPLY_SOURCE_LABELS.import} · ${ukDate(q.rep_price.at)}` : ''}</span>
        {can.see_costs && q.cost && <span className="rp-t2">cost {figure(q.cost, q.per)}</span>}
      </>
    );
    const rowActs = (q) => (
      <span className="rp-row-acts">
        <button type="button" className="rp-btn rp-ok" disabled={busy || !!reviewBlock} title={reviewBlock || 'Approve — confirms the sale price on the order'}
          onClick={() => setReview({ action: 'approve', requests: [q] })}><span className="material-symbols-rounded">verified</span>Approve</button>
        <button type="button" className="rp-btn" disabled={busy || !!reviewBlock || q.held} title={q.held ? 'Already held' : (reviewBlock || 'Hold for review — nothing on the order changes')}
          onClick={() => setReview({ action: 'hold', requests: [q] })}><span className="material-symbols-rounded">pause_circle</span>Hold</button>
        <button type="button" className="rp-btn" disabled={busy || !!sendBlock([q])} title={sendBlock([q]) || 'Ask again — a second price check, with their earlier price shown'}
          onClick={() => openSend([q])}><span className="material-symbols-rounded">forward_to_inbox</span>Ask again…</button>
        {repKey === REP_ALL && <span className="rp-chip">{q.rep_name || 'No rep'}</span>}
      </span>
    );
    return {
      body: (
        <>
          {myAsks.length > 0 && (
            <div className="rp-asks">
              <div className="rp-grp info"><span className="material-symbols-rounded">handshake</span>Contract asked for · {myAsks.length} — the rep asked for their price to be made a contract price</div>
              {myAsks.map(a => (
                <div key={a.key} className="rp-ask">
                  <div className="rp-c">
                    <span className="rp-t1">{a.customer?.account_no && <span className="account-badge-inline">{a.customer.account_no}</span>} {a.customer?.name}</span>
                    <span className="rp-t2">{a.product?.name}{a.product?.code ? <span className="rp-muted"> · {a.product.code}</span> : null}</span>
                  </div>
                  <div className="rp-c">
                    <span className="rp-t2">{a.lines.map((l, i) => <span key={l.line_id}>{i ? ' · ' : ''}<button type="button" className="rp-link" onClick={openOrder(l.order_id)}>{l.order_number}</button>{l.delivery_date ? ` ${ukDate(l.delivery_date)}` : ''}</span>)}</span>
                    <span className="rp-t2 rp-muted">{a.rep_name || 'Rep'} · {a.at ? ukDate(a.at) : ''}{a.source === 'import' ? ' · in their spreadsheet' : a.source === 'rep_view' ? ' · on their screen' : ''}</span>
                  </div>
                  <div className="rp-c rp-ask-note">{a.note ? <span className="rp-t2">“{a.note}”</span> : <span className="rp-muted">No note</span>}</div>
                  <div className="rp-c rp-right"><span className="rp-num rp-price">{figure(a.price) || 'no price'}</span><span className="rp-t2">rep's price</span></div>
                  <div className="rp-c rp-acts">
                    <span className="rp-row-acts">
                      <button type="button" className="rp-btn rp-ok" disabled={busy || !!contractBlock} title={contractBlock || 'Create or amend a contract with the rep\'s price'}
                        onClick={() => openContract(a)}><span className="material-symbols-rounded">handshake</span>Contract this price…</button>
                      <button type="button" className="rp-btn" disabled={busy || !!writeBlock} title={writeBlock || 'Put the rep\'s request aside — no contract'}
                        onClick={() => setAsk({ kind: 'notNow', ask: a })}><span className="material-symbols-rounded">do_not_disturb_on</span>Not now</button>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!priced.length ? <div className="rp-empty">No prices back from {repName || 'reps'} waiting for the office.</div> : (
            <>
              <div className="rp-listhead">{tickAll(priced)}<span className="rp-muted">Newest first. Prices from the reps' spreadsheets and their own pricing screens; nothing on the order changes until approved.</span></div>
              {priced.map(q => requestRow(q, { right: priceRight(q), actions: rowActs(q) }))}
            </>
          )}
        </>
      ),
      foot: (
        <>
          <span className="rp-muted">{chosen.length ? `${chosen.length} ticked` : `${priced.length} request${priced.length === 1 ? '' : 's'}`} · {lines} line{lines === 1 ? '' : 's'}</span>
          <span className="rp-foot-btns">
            <div><button type="button" className="btn-slideout always-out" disabled={busy || !!sendBlock(list)} title={sendBlock(list) || 'Ask again — a second price check'}
              onClick={() => openSend(list)}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">forward_to_inbox</span></div>
              <span className="btn-label">Ask again ({lines})…</span>
            </button></div>
            <div><button type="button" className="btn-slideout always-out" disabled={busy || !!reviewBlock || !list.length} title={reviewBlock || 'Hold for review'}
              onClick={() => setReview({ action: 'hold', requests: list })}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">pause_circle</span></div>
              <span className="btn-label">Hold ({lines})…</span>
            </button></div>
            <div><button type="button" className="btn-slideout always-out btn-invert" disabled={busy || !!reviewBlock || !list.length} title={reviewBlock || 'Approve — confirms the sale price on each line'}
              onClick={() => setReview({ action: 'approve', requests: list })}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">verified</span></div>
              <span className="btn-label">Approve ({lines})…</span>
            </button></div>
          </span>
        </>
      )
    };
  };

  const flagRow = (f, { done = false } = {}) => {
    const l = f.line || {};
    return (
      <div key={f.id} className={`rp-flag ${done ? 'done' : ''}`}>
        <span className="material-symbols-rounded rp-flag-icon">{done ? 'task_alt' : 'flag'}</span>
        <div className="rp-c">
          <span className="rp-t1">{l.customer?.account_no && <button type="button" className="account-badge-inline rp-acc" onClick={openContact(l.customer.id)}>{l.customer.account_no}</button>}
            <button type="button" className="rp-link rp-name" onClick={openContact(l.customer?.id)}>{l.customer?.name}</button></span>
          <span className="rp-t2">{l.product?.name || l.product?.code}</span>
          <span className="rp-t2"><button type="button" className="rp-link" onClick={openOrder(l.order_id)}>{l.order_number}</button>
            {l.delivery_date ? ` · ${ukDate(l.delivery_date)}` : ''}
            {f.invoice ? <> · invoiced <button type="button" className="rp-link" onClick={openInvoice(f.invoice.id)}>{f.invoice.invoice_no}</button></> : ''}
            {f.sale_price !== null && f.sale_price !== undefined ? ` at ${money(f.sale_price)}` : ''}</span>
        </div>
        <div className="rp-c">
          <span className="rp-t1">“{f.note}”</span>
          <span className="rp-t2">{f.kind === 'pull_back' ? 'Pulled back to price again by ' : ''}{f.by || f.rep_name || 'the rep'}{f.at ? ` · ${formatDateTimeLondon(f.at)}` : ''}</span>
          {!done && f.confirmed_after && (
            <span className="rp-warn"><span className="material-symbols-rounded">warning</span>
              Sale price confirmed {money(f.confirmed_after.value)}{f.confirmed_after.by ? ` by ${f.confirmed_after.by}` : ''} after this flag — check it (this is in the Alerts)</span>
          )}
          {done && <span className="rp-t2 rp-ok-text">{resolvedText(f)}</span>}
        </div>
        <div className="rp-c rp-acts">
          {!done && (
            <span className="rp-row-acts">
              <button type="button" className="rp-btn" onClick={openOrder(l.order_id)}><span className="material-symbols-rounded">open_in_new</span>Open the order</button>
              <button type="button" className="rp-btn" disabled={!f.invoice} title={f.invoice ? `Open ${f.invoice.invoice_no}` : 'Not invoiced'}
                onClick={f.invoice ? openInvoice(f.invoice.id) : undefined}><span className="material-symbols-rounded">request_quote</span>Open the invoice</button>
              <button type="button" className="rp-btn rp-ok" disabled={busy || !!writeBlock} title={writeBlock || 'Mark it dealt with — choose the document that settled it'}
                onClick={() => setResolving(f)}><span className="material-symbols-rounded">task_alt</span>Dealt with…</button>
            </span>
          )}
        </div>
      </div>
    );
  };

  const flaggedTab = () => ({
    body: (
      <>
        {data.ready.rep_pricing === false && <p className="rp-note warn"><span className="material-symbols-rounded">construction</span>{data.not_ready_reason}</p>}
        {!myFlags.length ? <div className="rp-empty">No open flags from {repName || 'reps'}.</div> : myFlags.map(f => flagRow(f))}
        {showResolved && (
          <>
            <div className="rp-grp"><span className="material-symbols-rounded">history</span>Dealt with — the last 60 days</div>
            {resolved.loading ? <LoadingSpinner /> : resolved.error ? <p className="rp-note err">{resolved.error}</p>
              : !resolved.flags.length ? <div className="rp-empty">None dealt with yet.</div>
                : filterFlags(resolved.flags, filters).map(f => flagRow(f, { done: true }))}
          </>
        )}
      </>
    ),
    foot: (
      <>
        <span className="rp-muted">A flag shows here and on its line. It is in the Alerts only if the line's price is confirmed while the flag is still open.</span>
        <span className="rp-foot-btns">
          <div className="checkbox-container rp-toggle">
            <div className="check light">
              <input id="rp-show-resolved" type="checkbox" checked={!!showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
              <label htmlFor="rp-show-resolved"></label>
            </div>
            <label htmlFor="rp-show-resolved" className="rp-toggle-label">Show dealt with</label>
          </div>
        </span>
      </>
    )
  });

  const sendsTab = () => ({
    body: repKey === REP_NONE ? <div className="rp-empty">Sends always go to a rep — set a rep on these customers to send.</div> : (
      <RepRequestHistory repId={repKey === REP_ALL ? null : Number(repKey)} canResend={!readOnly && !!can.confirm_sales}
        onSendAgain={(send, ids) => setSendLines({ ids, costs: {}, resendOf: /^\d+$/.test(String(send.id)) ? Number(send.id) : null })} />
    ),
    foot: <span className="rp-muted">Download the spreadsheet as it went, Resend it unchanged, or Send again with today's figures.</span>
  });

  const pane = tab === 'waiting' ? waitingTab() : tab === 'priced' ? pricedTab() : tab === 'flagged' ? flaggedTab() : tab === 'sends' ? sendsTab() : sendTab();
  const presets = useMemo(() => rangePresets || monthPresets(), [rangePresets]);
  const lastSend = entry && entry.last_send;

  return (
    <div className="search-customer-orders rep-pricing" style={{ '--page-title': "'Rep Pricing'" }}>
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Everything sent to reps, by the account's rep · delivering {ukDate(filters.from)} – {ukDate(filters.to)}</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>Choose a rep on the left. To send: lines with no price and not with a rep, delivered first. Waiting: sent and not back. Priced by rep: approve, hold or ask again. Flagged: the rep's notes to the office.</span>
          </span>
        </div>
        {/* One line, as the Lines view's heading (Cam, 30 Oct 2026 — q21):
            Clear Filters left of the switch, Import rep replies and Refresh
            right of it — never wrapped below. */}
        <div className="search-header-inner rp-head-controls" style={{ gridColumn: '2' }}>
          <div className="buttons rp-clear-filters">
            <div>
              <button type="button" className={clearFiltersClass(filtersApplied)} onClick={clearFilters} title={filtersApplied ? 'Clear the search, rep and product filters' : 'No filters applied'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">filter_alt_off</span></div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
          </div>
          <PricingCostingViewSwitch view="reps" onChange={onView} toSend={dueCount} />
          <div className="buttons rp-header-buttons">
            <div>
              <button type="button" className="btn-slideout" onClick={() => setImporting(true)} disabled={!!writeBlock || data.ready.pricing_costing === false}
                title={writeBlock || (data.ready.pricing_costing === false ? 'Not ready — migration pricing_costing.sql has not run' : 'Read a rep\'s filled-in price request spreadsheet')}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">upload_file</span></div>
                <span className="btn-label">Import rep replies</span>
              </button>
            </div>
            <div>
              <button type="button" className="btn-slideout" onClick={load} disabled={loading} title="Read it all again">
                <div className="icon-wrapper"><span className="material-symbols-rounded">refresh</span></div>
                <span className="btn-label">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row rp-filter-row">
          <div className="filter-group">
            <label>Search <SearchHelp fields={['customer', 'account no.', 'product', 'item code', 'additives', 'order no.', 'supplier', 'rep']} /></label>
            <Input type="text" placeholder="Space-separated terms — all must match" value={filters.search || ''} icon="search"
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))} />
          </div>
          <DateRangeFilter from={filters.from} to={filters.to} preset={filters.preset} presets={presets}
            onChange={({ from: f, to, preset }) => setFilters(prev => ({ ...prev, from: f, to, preset }))} />
          <div className="filter-group">
            <label>Products</label>
            <MultiSelectDropdown
              options={PRODUCT_CATEGORIES.map(c => ({ value: c.key, label: c.label }))}
              value={filters.categories || []}
              onChange={(vals) => setFilters(prev => ({ ...prev, categories: vals }))}
              placeholder="All products" tickBoxes hideTags
              summarize={(vals) => (vals.length === 0 ? 'All products' : vals.length === 1 ? (PRODUCT_CATEGORIES.find(c => c.key === vals[0])?.label || '1 category') : `${vals.length} categories`)}
            />
          </div>
        </div>
      </div>

      {loadError && <p className="rp-note err rp-banner"><span className="material-symbols-rounded">error</span>{loadError} <button type="button" className="rp-link" onClick={load}>Try again</button></p>}
      {data.loaded && data.ready.rep_pricing === false && (
        <p className="rp-note warn rp-banner"><span className="material-symbols-rounded">construction</span>{data.not_ready_reason} — reps' own prices and flags are not kept yet.</p>
      )}

      <div className="rp-body">
        <nav className="rp-rail" aria-label="Reps">
          <div className="rp-rail-h">Reps{(filters.reps || []).length ? <span className="rp-muted"> · {filters.reps.length} chosen in Lines</span> : null}</div>
          {!data.loaded && loading ? <LoadingSpinner /> : rail.map(e => {
            const c = e.counts;
            return (
              <button key={e.key} type="button" className="rp-rail-item" aria-pressed={e.key === (entry && entry.key)} onClick={() => setRepKey(e.key)}>
                <span className="rp-rn"><span>{e.name}</span>
                  {c.flagged > 0 && <span className="rp-cnt err">{c.flagged} flag{c.flagged === 1 ? '' : 's'}</span>}</span>
                <span className="rp-rc">
                  <span>{c.to_send.requests} to send</span>
                  {c.to_send.delivered > 0 && <span className="rp-due">{c.to_send.delivered} delivered</span>}
                  {c.waiting.requests > 0 && <span>{c.waiting.requests} waiting{c.waiting.since ? ` since ${ukDate(c.waiting.since)}` : ''}</span>}
                  {c.priced.requests > 0 && <span>{c.priced.requests} priced</span>}
                  {c.contract > 0 && <span>{c.contract} contract asked</span>}
                  {e.key === REP_NONE && c.to_send.requests > 0 && <span className="rp-due">set a rep to send</span>}
                </span>
              </button>
            );
          })}
        </nav>

        <section className="rp-main">
          <div className="rp-top">
            <h5>{entry ? entry.name : 'All reps'}</h5>
            <span className="rp-muted">
              {entry && entry.key === REP_ALL ? `${rail.length - 1} rep${rail.length === 2 ? '' : 's'} · ${dueCount} request${dueCount === 1 ? '' : 's'} to send`
                : [entry && entry.email, lastSend ? `last send ${formatDateTimeLondon(lastSend.at)}` : null].filter(Boolean).join(' · ')}
            </span>
            {loading && data.loaded && <span className="rp-muted"> · updating…</span>}
          </div>
          <div className="rp-tabs" role="tablist">
            {TABS.map(t => (
              <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}>
                <span className="material-symbols-rounded">{t.icon}</span>{t.label}
                {counts[t.key] !== null && <span className={`rp-cnt ${t.key === 'flagged' && counts[t.key] ? 'err' : t.key === 'send' && counts[t.key] ? 'warn' : ''}`}>{counts[t.key]}</span>}
              </button>
            ))}
          </div>
          <div className="rp-panel">{!data.loaded && loading ? <LoadingSpinner /> : pane.body}</div>
          <div className="rp-foot">{pane.foot}</div>
        </section>
      </div>

      {toast && (
        <div className="rp-toast" role="status">
          <span>{toast.message}</span>
          {toast.undo && <button type="button" className="rp-link" onClick={() => { const u = toast.undo; setToast(null); u(); }}>Undo</button>}
          <button type="button" className="rp-link" onClick={() => setToast(null)} aria-label="Close"><span className="material-symbols-rounded">close</span></button>
        </div>
      )}
      <AlertDialog isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert(a => ({ ...a, isOpen: false }))} />
      <ConfirmDialog
        isOpen={!!ask}
        title="Not now?"
        type="info"
        message={ask && ask.kind === 'notNow' ? `Put aside ${ask.ask.rep_name || 'the rep'}'s request to make ${ask.ask.customer?.name}'s ${ask.ask.product?.name || 'product'} price a contract price? Nothing is changed on the orders; the rep's price stays as it is.` : ''}
        confirmText="Not now"
        confirmIcon="do_not_disturb_on"
        onConfirm={() => notNow(ask.ask)}
        onCancel={() => setAsk(null)}
      />
      <ResendDialog resending={resending} onChange={setResending} onConfirm={doResend} onCancel={() => setResending(null)} />
      {sendLines && (
        <RepSendModal lineIds={sendLines.ids} costs={sendLines.costs} resendOf={sendLines.resendOf || null}
          onClose={(sent) => { setSendLines(null); if (sent) setTicked({}); load(); }} />
      )}
      {review && (
        <RepReviewDialog action={review.action} requests={review.requests}
          onClose={() => setReview(null)} onDone={(body) => reviewDone(review.action, body)} />
      )}
      {resolving && (
        <RepFlagResolveModal flag={resolving} onClose={() => setResolving(null)}
          onDone={(body) => { setResolving(null); showToast(`Flag dealt with${body.document ? ` — settled by ${body.document.ref}` : ''}`); load(); }} />
      )}
      {importing && (
        <RepRepliesImportModal reviewReady={!!data.ready.rep_review}
          lineFacts={Object.fromEntries(Object.entries(costsOf(data.requests)).map(([id, cost]) => [id, { cost }]))}
          onClose={() => { setImporting(false); load(); }}
          onDone={(message, undoItems = [], stayOpen = false) => {
            if (!stayOpen) setImporting(false);
            if (message) showToast(message, undoItems.length ? () => undoConfirm(undoItems) : null);
            load();
          }} />
      )}
      {contractLines && (
        <ContractPricesModal lines={contractLines.lines} notLines={contractLines.notLines}
          onClose={() => { setContractLines(null); load(); }}
          onDone={(message) => { setContractLines(null); if (message) showToast(message); load(); }} />
      )}
    </div>
  );
}

export default RepPricingView;
